import React from "react";

export type EditParticipantMode = "edit" | "preview";

interface EditParticipantContextType {
  mode: EditParticipantMode;
  setMode: (mode: EditParticipantMode) => void;
  modifiedData: any | null;
  /** A regisztráció id-ja, amelyhez a modifiedData tartozik (stale adat felismeréséhez). */
  modifiedDataRegistrationId: number | null;
  setModifiedData: (data: any | null, registrationId?: number | null) => void;
  isUpdating: boolean;
  setIsUpdating: (isUpdating: boolean) => void;
  formikRef: React.RefObject<{
    submitForm: () => void;
    getValues: () => {};
    setValues: (values: any) => void;
    setFieldValue: (field: string, value: any) => void;
    validateForm: () => Promise<any>;
    setTouched: (touched: any) => void;
  }>;
  formRef: React.RefObject<HTMLFormElement | null>;
  onPreview: () => void;
  onUpdate: () => void;
  onReset: () => void;
  setHandlers: (handlers: {
    onUpdate?: () => void;
    onPreview?: () => void;
    onReset?: () => void;
  }) => void;
  resetHandlers: () => void;
  reset: () => void;
}

export const EditParticipantContext =
  React.createContext<EditParticipantContextType | null>(null);

export const useEditParticipantContext = () => {
  const context = React.useContext(EditParticipantContext);
  if (!context) {
    return null;
  }
  return context;
};

export const EditParticipantProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [mode, setMode] = React.useState<EditParticipantMode>("edit");
  const [modifiedData, setModifiedDataState] = React.useState<any | null>(
    null
  );
  const [modifiedDataRegistrationId, setModifiedDataRegistrationId] =
    React.useState<number | null>(null);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

  const setModifiedData = React.useCallback(
    (data: any | null, registrationId: number | null = null) => {
      setModifiedDataState(data);
      setModifiedDataRegistrationId(data == null ? null : registrationId);
    },
    []
  );
  const formikRef = React.useRef<{
    submitForm: () => void;
    getValues: () => {};
    setValues: (values: any) => void;
    setFieldValue: (field: string, value: any) => void;
    validateForm: () => Promise<any>;
    setTouched: (touched: any) => void;
  }>(null);
  const formRef = React.useRef<HTMLFormElement | null>(null);

  const [onUpdate, setOnUpdate] = React.useState<() => void>(() => () => {});
  const [onPreview, setOnPreview] = React.useState<() => void>(() => () => {});
  const [onReset, setOnReset] = React.useState<() => void>(() => () => {});

  // const onPreview = React.useCallback(() => {
  //   setMode("preview");

  // }, [setMode]);

  const setHandlers = React.useCallback(
    (handlers: {
      onUpdate?: () => void;
      onPreview?: () => void;
      onReset?: () => void;
    }) => {
      if (handlers.onUpdate) setOnUpdate(() => handlers.onUpdate!);
      if (handlers.onPreview) setOnPreview(() => handlers.onPreview!);
      if (handlers.onReset) setOnReset(() => handlers.onReset!);
    },
    []
  );

  const resetHandlers = React.useCallback(() => {
    setOnUpdate(() => () => {});
    setOnPreview(() => () => {});
    setOnReset(() => () => {});
  }, []);

  const reset = React.useCallback(() => {
    setMode("edit");
    setModifiedData(null);
    // Call the onReset handler if it's set (to reset the form values)
    // Ne reseteljük a handler-eket, mert azok újra be lesznek állítva a setHandlers által
    onReset();
  }, [setMode, setModifiedData, onReset]);

  const value = React.useMemo<EditParticipantContextType>(
    () => ({
      mode,
      setMode,
      modifiedData,
      modifiedDataRegistrationId,
      setModifiedData,
      isUpdating,
      setIsUpdating,
      formikRef,
      formRef,
      onPreview,
      onUpdate,
      onReset,
      setHandlers,
      resetHandlers,
      reset,
    }),
    [
      mode,
      modifiedData,
      modifiedDataRegistrationId,
      setModifiedData,
      isUpdating,
      onPreview,
      onUpdate,
      onReset,
      setHandlers,
      resetHandlers,
      reset,
    ]
  );

  return (
    <EditParticipantContext.Provider value={value}>
      {children}
    </EditParticipantContext.Provider>
  );
};
